import ConfirmDialog from '@/components/ui/ConfirmDialog';
import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Plus, Trash2, Send, Users, Award, TrendingUp, CheckCircle, Clock, Mail, XCircle } from 'lucide-react';
import LeaderForm from '@/components/slt/LeaderForm';
import QuestionForm from '@/components/slt/QuestionForm';

export default function SLTInsights() {
  const [leaders, setLeaders] = useState([]);
  const [removePrompt, setRemovePrompt] = useState(null);
  const removeReturnRef = useRef(null);
  const headingRef = useRef(null);
  const [sendError, setSendError] = useState('');
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaderForm, setShowLeaderForm] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('leaders');
  const [liveMsg, setLiveMsg] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    try {
      const [leaderData, responseData, questionData] = await Promise.all([
        base44.entities.SeniorLeader.list('-created_date', 200),
        base44.entities.SLTResponse.list('-created_date', 200),
        base44.entities.SLTQuestion.list('-created_date', 200),
      ]);
      setLeaders(leaderData);
      setResponses(responseData);
      setQuestions(questionData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function deleteLeader(id) {
    await base44.entities.SeniorLeader.update(id, { active: false });
    setLeaders(prev => prev.filter(l => l.id !== id));
    setLiveMsg('Leader removed'); removeReturnRef.current = headingRef.current;
  }

  async function deleteQuestion(id) {
    await base44.entities.SLTQuestion.delete(id);
    setQuestions(prev => prev.filter(q => q.id !== id));
    setLiveMsg('Question deleted'); removeReturnRef.current = headingRef.current;
  }

  async function handleSendNow() {
    setSending(true); setSendError('');
    try {
      const active = leaders.filter(l => l.active !== false);
      if (active.length === 0) { setSendError('Add at least one active leader first.'); return; }
      if (questions.filter(q => q.is_active !== false).length === 0) { setSendError('No active questions in the question bank. Add questions first.'); return; }
      const res = await base44.functions.invoke('sendSLTQuestions', {});
      const sent = res.data?.sent ?? 0;
      setLiveMsg(`${sent} knowledge-check email${sent === 1 ? '' : 's'} sent. Each leader gets a single-use link — no account needed.`);
      await loadAll();
    } catch (e) {
      console.error(e);
      setSendError(e.message || 'Failed to send questions');
    } finally {
      setSending(false);
    }
  }


  const activeLeaders = leaders.filter(l => l.active !== false);
  const avgScore = activeLeaders.length > 0
    ? Math.round(activeLeaders.reduce((sum, l) => sum + (l.accuracy_score || 0), 0) / activeLeaders.length)
    : 0;
  const pendingCount = responses.filter(r => r.status === 'pending').length;
  const answeredCount = responses.filter(r => r.status === 'answered').length;
  const correctCount = responses.filter(r => r.is_correct === true).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {removePrompt && <ConfirmDialog title={removePrompt.title} description={removePrompt.description} returnFocusRef={removeReturnRef} onClose={() => setRemovePrompt(null)} onConfirm={removePrompt.action} />}
      {sendError && <p role="alert" className="accessible-error rounded border border-destructive p-3 text-destructive">{sendError}</p>}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{liveMsg}</div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="text-xl font-bold text-foreground">Leadership</h1>
          <p className="text-sm text-muted-foreground">Add your organisation’s leaders here. Once added, they will receive questions to help assess their skills and identify any areas where additional training may be needed.</p>
        </div>
        <button
          onClick={handleSendNow}
          disabled={sending || activeLeaders.length === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-40 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
        >
          <Send className="w-4 h-4" aria-hidden="true" />
          {sending ? 'Sending…' : 'Send Questions Now'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={<Users className="w-4 h-4 text-teal-600" />} label="Active Leaders" value={activeLeaders.length} />
        <StatCard icon={<Award className="w-4 h-4 text-amber-500" />} label="Avg Accuracy" value={`${avgScore}%`} />
        <StatCard icon={<CheckCircle className="w-4 h-4 text-teal-600" />} label="Correct Answers" value={correctCount} />
        <StatCard icon={<Clock className="w-4 h-4 text-amber-500" />} label="Pending" value={pendingCount} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          ['leaders', 'Leaders', Users],
          ['questions', 'Question Bank', TrendingUp],
          ['responses', 'Responses', Mail],
        ].map(([key, label, Icon]) => {
          const LIcon = Icon;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${
                tab === key ? 'border-teal-600 text-teal-700' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <LIcon className="w-4 h-4" aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Leaders tab */}
      {tab === 'leaders' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Your leaders</h2>
            <button
              onClick={() => { setEditingLeader(null); setShowLeaderForm(true); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add Leader
            </button>
          </div>

          {activeLeaders.length === 0 ? (
            <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
              <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">No leaders yet. Add your leaders to start sending knowledge checks.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeLeaders.map(leader => (
                <div key={leader.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                    <span className="text-teal-700 font-bold text-sm">{(leader.full_name || '?').charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{leader.full_name}</p>
                    <label className="block text-sm">Job title for {leader.full_name}<input id={`leader-role-${leader.id}`} readOnly value={leader.role_title || 'Not provided'} className="mt-1 block min-h-11 w-full rounded border border-border bg-background px-2 text-foreground" /></label>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-lg font-bold ${(leader.accuracy_score || 0) >= 70 ? 'text-teal-600' : (leader.accuracy_score || 0) >= 40 ? 'text-amber-600' : 'text-slate-500'}`}>
                      {leader.accuracy_score || 0}%
                    </p>
                    <p className="text-[10px] text-muted-foreground">{leader.questions_answered || 0} answered</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button onClick={() => { setEditingLeader(leader); setShowLeaderForm(true); }} aria-label={`Edit ${leader.full_name}, ${leader.role_title || 'job title not provided'}`} aria-describedby={`leader-role-${leader.id}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <Plus className="w-3.5 h-3.5 rotate-45" aria-hidden="true" />
                    </button>
                    <button onClick={event => { removeReturnRef.current = event.currentTarget; setRemovePrompt({ title: `Remove ${leader.full_name}?`, description: 'They will no longer receive leadership questions.', action: () => deleteLeader(leader.id) }); }} aria-label={`Remove ${leader.full_name}, ${leader.role_title || 'job title not provided'}`} aria-describedby={`leader-role-${leader.id}`} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Questions tab */}
      {tab === 'questions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Question Bank ({questions.length})</h2>
            <button
              onClick={() => { setEditingQuestion(null); setShowQuestionForm(true); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add Question
            </button>
          </div>

          {questions.length === 0 ? (
            <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
              <p className="text-sm text-muted-foreground">No questions yet. Add questions about the Equality Act, Disability Confident scheme, or accessibility.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {questions.map(q => (
                <div key={q.id} className="bg-card border border-border rounded-xl p-4 group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-2 inline-block ${q.category === 'Equality Act 2010' ? 'bg-blue-100 text-blue-700' : q.category === 'Disability Confident Scheme' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>
                        {q.category}
                      </span>
                      <p className="text-sm font-medium text-foreground mb-1">{q.question_text}</p>
                      <p className="text-xs text-teal-600 font-medium">✓ {q.correct_answer}</p>
                      <p className="text-xs text-muted-foreground mt-1">{q.options?.length || 0} options</p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
                      <button onClick={() => { setEditingQuestion(q); setShowQuestionForm(true); }} aria-label="Edit question" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                        <Plus className="w-3.5 h-3.5 rotate-45" aria-hidden="true" />
                      </button>
                      <button onClick={event => { removeReturnRef.current = event.currentTarget; setRemovePrompt({ title: 'Delete this question?', description: q.question_text, action: () => deleteQuestion(q.id) }); }} aria-label="Delete question" className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Responses tab */}
      {tab === 'responses' && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground mb-2">Recent Responses ({responses.length})</h2>
          {responses.length === 0 ? (
            <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
              <p className="text-sm text-muted-foreground">No responses yet. Questions will appear here once leaders answer them.</p>
            </div>
          ) : (
            responses.slice(0, 50).map(r => (
              <div key={r.id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${r.status === 'answered' ? (r.is_correct ? 'bg-teal-100' : 'bg-amber-100') : 'bg-slate-100'}`}>
                  {r.status === 'answered' ? (
                    r.is_correct ? <CheckCircle className="w-4 h-4 text-teal-600" /> : <XCircle className="w-4 h-4 text-amber-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{r.leader_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.question_text}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 ${r.status === 'pending' ? 'bg-slate-100 text-slate-600' : r.is_correct ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>
                  {r.status === 'pending' ? 'Pending' : r.is_correct ? 'Correct' : 'Incorrect'}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Forms */}
      {showLeaderForm && (
        <LeaderForm
          leader={editingLeader}
          onClose={() => { setShowLeaderForm(false); setEditingLeader(null); }}
          onSaved={(name, edited) => { setShowLeaderForm(false); setEditingLeader(null); setLiveMsg(`${name} ${edited ? 'updated' : 'added'}.`); void loadAll(); }}
        />
      )}
      {showQuestionForm && (
        <QuestionForm
          question={editingQuestion}
          onClose={() => { setShowQuestionForm(false); setEditingQuestion(null); }}
          onSaved={() => { setShowQuestionForm(false); setEditingQuestion(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-4 shadow-sm">
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}
